import i18n from "i18n";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import {
  createModelMapModelFromDocument,
  ModelMap,
  TModelError,
} from "modules/modelMap/models/ModelMap";
import { ModelMapService } from "modules/modelMap/ModelMapService";
import { observable, action, toJS } from "mobx";
import { ModelMapFilter } from "./models/ModelMapFilter";
import { ModelMapFormat } from "./models/ModelMapFormat";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { TakeOffService } from "modules/takeOff/TakeOffService";
import { LibraryStore } from "modules/library/LibraryStore";
import BaseStore from "../../common/BaseStore";
import { WBSStore } from "modules/WBS/WBSStore";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import { ToolbarStore } from "modules/WBS/ToolbarStore";
import { CommonStore } from "common/CommonStore";
import * as _ from "lodash";
import { evaluate } from "mathjs";
import { EModelMapTab, EModelMapType, ERightMenuTab } from "common/enums";
import axios from "axios";
/**
 * Store de Model-Maps
 * @category Model-Maps
 * @hideconstructor
 */

class ModelMapStore extends BaseStore {
  /** Valor pesquisado no painel de model-maps. */
  search: string = "";
  /** Lista de model-maps do projeto atual. */
  @observable modelMaps: FirebaseDocument<ModelMap>[] = [];
  /** Lista de model-maps a serem exibidos no painel considerando o valor pesquisado. */
  @observable modelMapsView: FirebaseDocument<ModelMap>[] = [];
  /** Model-map selecionado para modificação. */
  @observable modelMapSelected: FirebaseDocument<ModelMap> | undefined;
  /** Lista de model-maps selecionados no painel. */
  @observable modelMapCheck: FirebaseDocument<ModelMap>[] = [];
  /** Tipo selecionado para criar model-map ou tipo do model-map selecionado para modificação. */
  @observable typeSelected?: EModelMapType;
  /** Lista de linhas da formatação do model-map selecionado. */
  @observable modelMapFormat: ModelMapFormat[] = new Array();
  /** Linha da formatação atual selecionada.
   *  Contém as propriedades target (elemento HTML) e line (dados da linha)
   */
  @observable selectedFormatLine?: { target: any; line: ModelMapFormat };
  /** Flag para quando o WBS atual precisa ser atualizado. */
  @observable currentWbsNeedToBeUpdated: boolean = false;
  /** Flag usada para atualizar dados de um model-map antigo. */
  oldProjectUpdated = false;
  /** Variável que armazena uma cópia da estrutura da formatação antes de alterar. Talvez não seja utilizado!
   *  @deprecated
   */
  takeOffEditCompare: any = [];
  /** Variável que armazena os tipos, materiais e propriedades dos arquivos para evitar múltiplas consultas ao banco. */
  lastLibrariesEnabledWBS: any = {};
  /** Variável antiga utilizada na função que não sabemos se realmente está sendo utilizada.
   * @deprecated
   */
  lastFileSelection: string[];

  constructor() {
    super("ModelMapStore");
  }

  resetStore() {
    this.search = "";
    this.modelMaps = [];
    this.modelMapsView = [];
    this.modelMapSelected = undefined;
    this.typeSelected = undefined;
    this.lastFileSelection = [];
    this.modelMapFormat = new Array();
    this.selectedFormatLine = undefined;
    this.oldProjectUpdated = false;
    this.lastLibrariesEnabledWBS = {};
    this.currentWbsNeedToBeUpdated = false;
  }

  /**
   * Obtém os model-maps do projeto atual com snapshot atualizando
   * as listas de model-maps e de model-maps pesquisados. E se o model-map
   * modificado estiver selecionado no painel de model maps, este atualiza a variável de seleção.
   */
  fetchModelMaps() {
    this.clearModelMapsLists();
    ModelMapService.fetchModelMapsFromDataBase(
      DevelopmentStore.developmentId,
      async (snapshot) => {
        const modelMaps = this.modelMaps;
        const search = this.search;

        for (const change of snapshot.docChanges()) {
          const item = createModelMapModelFromDocument(change.doc);

          // Deleta do cache ao abrir o projeto ou ter alguma alteração de outro cliente
          if (TakeOffStore.groupDataModelMap) {
            TakeOffStore.groupDataModelMap[item.id] = undefined;
          }

          if (change.type === "added") {
            const fnCompare = (a, b) => a > b;
            CommonStore.pushSortingByField(
              modelMaps,
              item,
              ["data", "name"],
              fnCompare
            );
          } else {
            const itemIndex = modelMaps.findIndex((lib) => lib.id === item.id);
            if (itemIndex !== -1) {
              if (change.type === "removed") {
                modelMaps.splice(itemIndex, 1);
              } else {
                // Se o model map modificado estiver selecionado no painel de model maps, atualiza a seleção.
                if (
                  this.modelMapSelected !== undefined &&
                  this.modelMapSelected.id === item.id
                ) {
                  this.modelMapSelected = item;
                }
                modelMaps[itemIndex] = item;
              }
            }
          }
        }

        this.modelMaps = modelMaps;
        this.searchModelMaps(search);
      }
    );
  }

  private clearModelMapsLists() {
    this.modelMaps = new Array();
    this.modelMapsView = new Array();
    this.search = "";
  }

  async updateModelMapName(
    modelMap: FirebaseDocument<ModelMap>,
    newName: string
  ) {
    this.setModelMapSelected(undefined);
    if (newName !== modelMap.data.name) {
      await ModelMapService.changeModelMapName(modelMap, newName);
      modelMap.data.name = newName;
    }
  }

  @action
  setIsCurrentWbsNeedToBeUpdated() {
    this.currentWbsNeedToBeUpdated = true;
    let hasWarning = false;
    for (const modelMap of this.modelMapsView) {
      if (modelMap.data.costPlansNeedtoBeUpdated) {
        if (
          modelMap.data.costPlansNeedtoBeUpdated.includes(WBSStore.currentWbsId)
        ) {
          hasWarning = true;
        }
      }
    }
    this.currentWbsNeedToBeUpdated = hasWarning;
  }

  @action
  checkAllModelMaps(checked: boolean) {
    this.modelMapCheck = [];
    if (checked) {
      this.modelMapCheck.push(...this.modelMaps);
    }
  }

  toogleSelectedModelMap() {
    const modelMap = this.modelMapSelected;
    if (modelMap) {
      let index = this.modelMapCheck.findIndex((mm) => mm.id === modelMap.id);
      if (index > -1) this.modelMapCheck.splice(index, 1);
      else this.modelMapCheck.push(modelMap);
    }
  }

  modelMapsCheckedHasWarning(): boolean {
    return this.modelMapCheck.some(
      (m) =>
        this.isWarning(m) &&
        (!m.data.formatErrors || m.data.formatErrors.length === 0) &&
        (!m.data.filterErrors || m.data.filterErrors.length === 0)
    );
  }

  private isWarning(modelMap: FirebaseDocument<ModelMap>): boolean {
    if (modelMap.data.costPlansNeedtoBeUpdated) {
      return modelMap.data.costPlansNeedtoBeUpdated.includes(
        WBSStore.currentWbsId
      );
    }
    return false;
  }

  async isOldProjectUpdated(value: boolean) {
    this.oldProjectUpdated = value;
  }

  loadModelFormat() {
    let takeOffModelMap = TakeOffStore.takeOffModelMap;
    let formats =
      takeOffModelMap !== undefined &&
      takeOffModelMap.data.formats !== undefined
        ? takeOffModelMap.data.formats
        : [];
    let editFormat = new Array();
    for (const format of formats) {
      let mmFormat = new ModelMapFormat(
        format.index,
        format.parentIndex,
        format.childIndex,
        format.description,
        format.quantity,
        format.unit
      );
      editFormat.push(mmFormat);
    }
    TakeOffStore.isFormating = formats.length === 0;

    // Add linhas em branco
    for (let index = 0; index < 60; index++) {
      const hasElement = editFormat.some((line) => line.index === index);
      if (!hasElement) editFormat.push(this.newBlankFormat(index, -1, index));
    }

    this.selectedFormatLine = undefined;
    this.modelMapFormat = _.sortBy(editFormat, ["index"]);
  }

  private newBlankFormat(
    index: number,
    parentIndex: number,
    childIndex: number
  ) {
    return new ModelMapFormat(index, parentIndex, childIndex, "", "", "");
  }

  private InsertAt(elements, newElement, position: number) {
    let newArray;
    if (position === 0) {
      newArray = [...elements];
      newArray.unshift(newElement);
    } else if (position === elements.length - 1) {
      newArray = [...elements];
      newArray.push(newElement);
    } else {
      let part1 = elements.slice(0, position),
        part2 = elements.slice(position);
      part1.push(newElement);
      newArray = part1.concat(part2);
    }

    return [...newArray];
  }

  // *Nayara* - essa função foi alterada por causa das linhas em branco
  // que ficam com o valor do childIndex fora da sequencia
  private getSiblingAbove(elements, parentIndex, childIndex) {
    const siblingsAbove = _.filter(
      elements,
      (f) => f.parentIndex === parentIndex && f.childIndex < childIndex
    );
    return siblingsAbove ? siblingsAbove.pop() : undefined;
  }

  private getSiblingsBelow(elements, parentIndex, childIndex) {
    return _.filter(
      elements,
      (f) => f.parentIndex === parentIndex && f.childIndex > childIndex
    );
  }

  private refreshIndexes(elements, startIndex: number) {
    for (let i = elements.length - 1; i > startIndex && i >= 0; i--) {
      let oldId = elements[i].index;
      elements[i].index = i;
      // Verifica se tem filhos do elementos atual, se sim altera sua propriedade parentIndex
      this.setSiblingsParentId(elements, elements[i].parentIndex, oldId, i, i);
    }
  }
  // O parametro parentIndexRef é usado para evitar que os irmãos alterem o parentIndex um do outro
  private setSiblingsParentId(
    elements,
    parentIndexRef,
    oldId,
    newId,
    startIndex: number = -1
  ) {
    for (let i = startIndex + 1; i < elements.length; i++) {
      if (
        elements[i].parentIndex !== parentIndexRef &&
        elements[i].parentIndex === oldId
      ) {
        elements[i].parentIndex = newId;
      } else {
        // *Nayara* - Aqui estou considerando que os filhos estão logo após o elemento pai
        if (i === startIndex + 1) return;
      }
    }
  }

  private refreshSiblingsIndexes(
    elements,
    siblingsList,
    increment: boolean = false
  ) {
    for (const sibling of siblingsList) {
      let newIndex = increment
        ? sibling.childIndex + 1
        : sibling.childIndex - 1;
      elements[sibling.index].childIndex = newIndex;
    }
  }

  private restoreSiblingsIndexes(elements, siblingsList, parentIndex: number) {
    let newCount = 0;
    for (const sibling of siblingsList) {
      elements[sibling.index].childIndex = newCount;
      elements[sibling.index].parentIndex = parentIndex;
      newCount++;
    }
  }

  getFormatLevel(format: ModelMapFormat): number {
    let level = 0;
    let parentIndex = format.parentIndex;
    if (parentIndex === -1 || parentIndex === undefined) return level;

    while (parentIndex >= 0) {
      level += 1;
      let find = _.find(this.modelMapFormat, function (f) {
        return f.index === parentIndex;
      });
      if (find) {
        parentIndex = find.parentIndex;
      } else {
        parentIndex -= 1;
      }
    }
    return level;
  }
  getIsStage(format: ModelMapFormat): boolean {
    let isStage = _.some(this.modelMapFormat, function (f) {
      return f.parentIndex === format.index;
    });
    return isStage;
  }

  validateFormula(formula: string): boolean {
    try {
      const regexFields = /(\[[^\]]+\])/g;
      formula = formula.replace(regexFields, "1");

      const regexTexts = /(\"[^\"]+\")/g;
      formula = formula.replace(regexTexts, "1");

      if (i18n.language == "PT") {
        formula = formula.replace(/,/gi, ".");
      }

      evaluate(formula);
    } catch (error) {
      console.error("Error on validate formula", error);
      return false;
    }
    return true;
  }

  // PRUBK-802 - Habilitar os add linhas
  @action
  addNewFormatLine(operation: number) {
    if (!this.selectedFormatLine) return;
    let selectedFormat = this.selectedFormatLine.line;
    if (selectedFormat.description === "") return;

    let newLine,
      modelMapFormat = [...this.modelMapFormat],
      siblingsBelow = this.getSiblingsBelow(
        modelMapFormat,
        selectedFormat.parentIndex,
        selectedFormat.childIndex
      );

    // Add linha acima
    if (operation === 1) {
      newLine = this.newBlankFormat(
        selectedFormat.index,
        selectedFormat.parentIndex,
        selectedFormat.childIndex
      );
      siblingsBelow = [selectedFormat, ...siblingsBelow];
      // Add linha abaixo
    } else {
      // Se tiver irmaos abaixo subistitui o primeiro
      if (siblingsBelow && siblingsBelow.length) {
        newLine = this.newBlankFormat(
          siblingsBelow[0].index,
          siblingsBelow[0].parentIndex,
          siblingsBelow[0].childIndex
        );
      } else {
        newLine = this.newBlankFormat(
          selectedFormat.index + 1,
          selectedFormat.parentIndex,
          selectedFormat.childIndex + 1
        );
      }
    }

    if (siblingsBelow) {
      this.refreshSiblingsIndexes(modelMapFormat, siblingsBelow, true);
    }

    let newModelMapFormat = this.InsertAt(
      modelMapFormat,
      newLine,
      newLine.index
    );
    this.refreshIndexes(newModelMapFormat, newLine.index);

    this.modelMapFormat = [...newModelMapFormat];
  }

  // PRUBK-767 - Erro ao agrupar à direita
  private clearFathersData(element) {
    if (element) {
      element.quantity = "";
      element.unit = "";
    }
  }

  // PRUBK-802 - Separei as funcoes para ficar mais legível
  @action
  detachFormatLine() {
    if (!this.selectedFormatLine) return;
    let selectedFormat = this.selectedFormatLine.line,
      modelMapFormat = [...this.modelMapFormat];

    if (
      !selectedFormat ||
      selectedFormat.description === "" ||
      selectedFormat.parentIndex === -1
    )
      return;

    const parentFormat = modelMapFormat[selectedFormat.parentIndex];

    // Atualizar irmaos abaixo para filho do elemento selecionado
    let siblingsBelow = this.getSiblingsBelow(
      modelMapFormat,
      selectedFormat.parentIndex,
      selectedFormat.childIndex
    );
    this.restoreSiblingsIndexes(
      modelMapFormat,
      siblingsBelow,
      selectedFormat.index
    );

    // se tiver irmaos a baixo limpa os dados do elemento selecionado
    if (siblingsBelow && siblingsBelow.length) {
      this.clearFathersData(modelMapFormat[selectedFormat.index]);
    }

    // Atualizar irmaos do elemento pai
    let parentSiblingsBelow = this.getSiblingsBelow(
      modelMapFormat,
      parentFormat.parentIndex,
      parentFormat.childIndex
    );
    this.refreshSiblingsIndexes(modelMapFormat, parentSiblingsBelow, true);

    // Atualiza a hierarquia do elemento selecinado
    modelMapFormat[selectedFormat.index].parentIndex = parentFormat.parentIndex;
    modelMapFormat[selectedFormat.index].childIndex =
      parentFormat.childIndex + 1;

    this.modelMapFormat = [...modelMapFormat];
    (this.selectedFormatLine.target as HTMLElement).focus();
  }

  // PRUBK-802 - Separei as funcoes para ficar mais legível
  @action
  attachFormatLine() {
    if (!this.selectedFormatLine) return;
    let selectedFormat = this.selectedFormatLine.line,
      modelMapFormat = [...this.modelMapFormat],
      childIndex = 0;

    if (
      !selectedFormat ||
      selectedFormat.description === "" ||
      selectedFormat.childIndex === 0
    )
      return;

    let siblingAbove = this.getSiblingAbove(
      modelMapFormat,
      selectedFormat.parentIndex,
      selectedFormat.childIndex
    );
    let siblingsBelow = this.getSiblingsBelow(
      modelMapFormat,
      selectedFormat.parentIndex,
      selectedFormat.childIndex
    );

    // se elemento atual for primeiro filho
    if (siblingAbove && siblingAbove.index + 1 === selectedFormat.index) {
      this.clearFathersData(modelMapFormat[siblingAbove.index]);
    } else {
      // Obtem o ultimo filho do siblingAbove
      let newSiblingAbove = modelMapFormat[selectedFormat.index - 1];
      childIndex = newSiblingAbove.childIndex + 1;
    }

    // Atualiza a hierarquia do elemento selecinado
    modelMapFormat[selectedFormat.index].parentIndex = siblingAbove.index;
    modelMapFormat[selectedFormat.index].childIndex = childIndex;

    // Atualizar irmaos abaixo do elemento selecionado
    this.refreshSiblingsIndexes(modelMapFormat, siblingsBelow);

    this.modelMapFormat = [...modelMapFormat];
    (this.selectedFormatLine.target as HTMLElement).focus();
  }

  findModelMaponCostPlan(id) {
    let findInCP = _.find(WBSStore.dataTable, function (row) {
      return row.data.fromModelMap === id;
    });
    return findInCP !== undefined;
  }

  async saveModelMapsConfiguration(
    editing: boolean,
    name: string,
    allModels: boolean,
    ifcsLabels: string[],
    categories: string[]
  ) {
    // Compara o texto original com o label que foi criado
    categories = categories.map((cat) => {
      for (var prop in i18n.ifc) {
        if (i18n.ifc[prop] + " - " + prop === cat) {
          return prop;
        }
      }
      return cat;
    });

    let ifcsIds: string[] = [];
    if (allModels) {
      ifcsIds = LibraryStore.libraries.map((lib) => lib.id);
    } else {
      ifcsLabels.forEach((label) => {
        let lib = LibraryStore.findLibrayByLabelName(label);
        if (lib) ifcsIds.push(lib.id);
      });
    }

    if (editing) return this.editModelMap(name, allModels, ifcsIds, categories);
    return this.addModelMap(name, allModels, ifcsIds, categories);
  }

  private async editModelMap(
    name: string,
    allModels: boolean,
    filesIds: string[],
    categories: string[]
  ) {
    const modelMap = TakeOffStore.takeOffModelMap;
    if (!modelMap) return;

    let modelErrors: TModelError[] = [];
    // Se for usado todos os modelos não há esse erro
    if (!allModels) {
      // Filtra os que não estão selecionados
      modelErrors = (modelMap.data.modelErrors || []).filter((me) => {
        let lib = LibraryStore.findLibrayByLabelName(me.label);
        return Boolean(lib);
      });
    }

    try {
      if (TakeOffStore.groupDataModelMap) {
        TakeOffStore.groupDataModelMap[modelMap.id] = undefined;
      }

      modelMap.data.fields = [];
      modelMap.data.name = name;
      modelMap.data.types = categories;
      modelMap.data.files = filesIds;
      modelMap.data.categoryErrors = [];
      modelMap.data.modelErrors = modelErrors;
      modelMap.data.useAllModels = allModels;

      await this.updateModelMap(modelMap);

      if (modelMap) {
        this.modelMapSelected = modelMap;

        // Reseta variaveis das stores
        TakeOffStore.resetTakeOffProperties();
        this.resetModelMapFormatProperties();

        // Obtem as lista de fileItems
        await TakeOffService.fetchTakeOffList(this.modelMapSelected);
        this.loadModelFormat();

        // Roda a formatação e obtem o takeOff formatado atualizado
        await this.saveFormat(true, false);
        await TakeOffStore.fetchTakeOffFormatedList();

        // await TakeOffService.fetchTakeOffList(this.modelMapSelected);
      }
      TakeOffStore.setModelMapToFilter();
    } catch (e) {
      console.error("Error on editModelMap", e);
    }
  }

  private async addModelMap(
    name: string,
    allModels: boolean,
    filesIds: string[],
    categories: string[]
  ) {
    if (!this.typeSelected) return;
    try {
      const newModelMap = await ModelMapService.createModelMapOnDataBase(
        DevelopmentStore.developmentSelected.id,
        name,
        this.typeSelected,
        categories,
        filesIds,
        allModels
      );

      if (newModelMap) {
        this.modelMapSelected = newModelMap;

        // Reseta variaveis das stores
        TakeOffStore.resetTakeOffProperties();
        this.resetModelMapFormatProperties();

        // Obtem as lista de fileItems e takeOff formatados
        await TakeOffService.fetchTakeOffList(newModelMap);
        this.loadModelFormat();
        await TakeOffStore.fetchTakeOffFormatedList();

        // Atualiza as variaveis de model-map selecionado e obtem as propriedades novamente
        const modelMap = TakeOffStore.takeOffModelMap;
        if (modelMap) {
          this.setModelMapSelected(modelMap);
          this.fetchModelMapProperties(modelMap.data.type);
        }
        TakeOffStore.setLayerToEditModelMap();
      }
    } catch (error) {
      console.error("Error on addModelMap", error);
    }
  }

  @action
  async saveFormat(
    needUpdate: boolean = false,
    needShowSnack: boolean = true
  ): Promise<void> {
    // PRUBK-423 - Grava no model map um array tipado de números para setar o estilo de vários elementos simultaneamente
    const takeoffMM = TakeOffStore.takeOffModelMap as any;
    let idMM = -1;
    if (takeoffMM) {
      idMM = (TakeOffStore.takeOffModelMap as any).id;
    }

    // Remove os erros de formatação
    takeoffMM.data.formatErrors = [];

    // PRUBK-802 - Remove as linhas em branco antes de salvar
    let modelMapFormat = this.modelMapFormat.filter(
      (f) => f.description !== "" || f.quantity !== ""
    );
    let formats = _.orderBy(modelMapFormat, ["index"], ["asc"]);
    let newFormats = new Array();
    for (const format of formats) {
      newFormats.push({ ...format });
    }

    if (TakeOffStore.takeOffModelMap !== undefined) {
      TakeOffStore.takeOffModelMap.data.formats = _.sortBy(newFormats, [
        "index",
      ]);
    }

    // Se possui formatação, está incluído no plano de custo e a operação precisa ser atualizada
    // por alteração em filtro ou por alteração na configuração da formatação do modelo map
    if (
      this.takeOffEditCompare.length > 0 &&
      this.findModelMaponCostPlan(idMM) &&
      (needUpdate ||
        !_.isEqual(
          _.sortBy(this.modelMapFormat),
          _.sortBy(this.takeOffEditCompare)
        ))
    ) {
    }
    this.setIsCurrentWbsNeedToBeUpdated();
    // PRUBK-423 - Aguarda a finalização da atualização do objeto do model map com as labels dos elementos utilizados na formatação,
    // para então efetivar a gravação do model map no firebase
    await ModelMapService.updateModelMapIfcGUIDs(TakeOffStore.takeOffModelMap);
    let saveModelMapPromise = await ModelMapService.wbsThatNeedToBeUpdated(
      TakeOffStore.takeOffModelMap
    );

    await this.updateModelMap(saveModelMapPromise, true);
  }

  @action
  saveFilters(filters: Array<ModelMapFilter>, clearFiltersFromField): void {
    let modelMapFilters =
      TakeOffStore.takeOffModelMap !== undefined
        ? TakeOffStore.takeOffModelMap.data.filters
        : new Array();
    modelMapFilters = _.filter(modelMapFilters, function (obj) {
      return obj.field !== clearFiltersFromField;
    });

    for (const filter of filters) {
      modelMapFilters.push(filter);
    }

    modelMapFilters = _.orderBy(
      modelMapFilters,
      ["field", "logicalOperator"],
      ["asc", "asc"]
    );
    let newFilters = new Array();
    for (const mmFilter of modelMapFilters) {
      newFilters.push({ ...mmFilter });
    }

    if (TakeOffStore.takeOffModelMap !== undefined)
      TakeOffStore.takeOffModelMap.data.filters = newFilters;

    this.updateModelMap(TakeOffStore.takeOffModelMap);

    const newModelMapFilters = TakeOffService.applyFilter(
      modelMapFilters,
      TakeOffStore.takeOffItems
    );
    let filteredGUID: any = {};
    for (let i = 0; i < newModelMapFilters.length; i++) {
      const element = newModelMapFilters[i];
      if (filteredGUID[element.file.id]) {
        filteredGUID[element.file.id].push(element.GUID);
      } else {
        filteredGUID[element.file.id] = [];
        filteredGUID[element.file.id].push(element.GUID);
      }
    }

    TakeOffStore.takeOffFilteredItems = newModelMapFilters;
  }

  updateModelMap(
    modelMap?: FirebaseDocument<ModelMap>,
    format: boolean = false
  ) {
    if (!modelMap) return;
    ModelMapService.updateModelMapOnDataBase(modelMap, format);
  }

  @action
  setModelMapInternalFileArray(files: string[]) {
    this.lastFileSelection = files;
  }

  @action
  setModelMapPropertiesType(type: EModelMapType) {
    this.typeSelected = type;
  }

  @action
  setModelMapSelected(modelMap: FirebaseDocument<ModelMap> | undefined) {
    if (
      modelMap &&
      this.modelMapSelected &&
      this.modelMapSelected.id === modelMap.id
    ) {
      return;
    }

    this.modelMapSelected = modelMap;
  }

  @action
  clearModelMapSelected() {
    this.modelMapSelected = undefined;
  }

  async loadCategories(ifcs: string[]): Promise<any[]> {
    const modelMap = TakeOffStore.takeOffModelMap;
    const availableSelection: any = [];

    for (let i = 0; i < ifcs.length; i++) {
      const type = this.typeSelected as string;

      // Recupera os elementos disponiveis para esse modelo com esse tipo especifico que foi selecionado
      if (
        this.lastLibrariesEnabledWBS[ifcs[i]] &&
        this.lastLibrariesEnabledWBS[ifcs[i]][type]
      ) {
        availableSelection.push(
          ...this.lastLibrariesEnabledWBS[ifcs[i]][type][0]
        );
      } else {
        // Se ainda não foi carregado, carrega do banco o item especifico
        if (modelMap) {
          await this.fetchModelMapProperties(type as EModelMapType);

          if (
            this.lastLibrariesEnabledWBS[ifcs[i]] &&
            this.lastLibrariesEnabledWBS[ifcs[i]][type]
          ) {
            availableSelection.push(
              ...this.lastLibrariesEnabledWBS[ifcs[i]][type][0]
            );
          }
        }
      }
    }

    // Filtra apenas os elementos unicos
    return [...Array.from(new Set(availableSelection))];
  }

  /** Esta função verifica se os dados de tipos e propriedades dos arquivos já estão
   * carregado em memória, caso contrário, ele faz a consulta destes dados e salva.
   * @async
   */
  async fetchModelMapProperties(type: EModelMapType): Promise<void> {
    await LibraryStore.fetchLibraries();

    // Lista de arquivos de modelos disponíveis na biblioteca do projeto
    const files: any = LibraryStore.libraries.map((lib) => lib.id);

    // Array de promises para otimizar a recuperação das informação de elementos
    // construtivos, materiais e propriedades associadas dos elementos
    const filePromises = new Array<Promise<any>>();

    // Declaração de arrays para recuperar e agrupar as informações disponíveis
    // para seleção na criação do Model Map
    let typesAvailable = new Array();
    let typePropertiesAvailable = new Array();
    let materialsAvailable = new Array();
    let materialPropertiesAvailable = new Array();
    let fileIds = new Array();

    // Reseta no ModelMapStore as informações de elementos construtivos, materiais e propriedades
    await this.setModelMapPropertiesType(type);
    await this.setModelMapInternalFileArray(files);

    // Percorre os modelos do projeto
    for (const fileId of files) {
      let materialAvailable = undefined;
      let typeAvailable = undefined;

      // Recupera as informações do ModelMapStore se identificar que o modelo já foi adicionado anteriormente em memória
      if (
        this.lastLibrariesEnabledWBS[fileId] &&
        this.lastLibrariesEnabledWBS[fileId][type]
      ) {
        const [types, properties] = this.lastLibrariesEnabledWBS[fileId][type];

        if (type === "EC") {
          typeAvailable = types;
          typesAvailable = typesAvailable.concat(types);
          typePropertiesAvailable = typePropertiesAvailable.concat(properties);
        } else {
          materialAvailable = types;
          materialsAvailable = materialsAvailable.concat(types);
          materialPropertiesAvailable =
            materialPropertiesAvailable.concat(properties);
        }
      }

      // Se não encontrou as informações em store, deve recuperar do firebase
      if (
        (type === "EC" && typeAvailable == undefined) ||
        (type === "MAT" && materialAvailable == undefined)
      ) {
        // Adiciona o id do arquivo em array para reutilizar após o término de execução das promises
        fileIds.push(fileId);
        // Adiciona no array de promises apenas as chamadas necessárias de acordo com o tipo de model map
        filePromises.push(
          ModelMapService.fetchTypesOrMaterialsFromDataBase(
            DevelopmentStore.developmentSelected.id,
            type === "EC" ? "fileTypes" : "fileMaterials",
            fileId
          )
        );
      }
    }

    // Efetua verificação se foram adicionadas promises para recuperar informações do firebase
    // if (filePromises.length > 0) {
    // Aguarda a o término das promises para efetuar o ajuste das informações necessárias
    await Promise.all(filePromises).then((values) => {
      // Percorre o valor de retorno de cada promise
      for (const value of values) {
        const [types, properties] = value;
        // Recupera o id do arquivo ifc adicionado no momento da criação das promises
        const fileId = fileIds[values.indexOf(value)];

        // Define os valores dos elementos construtivo, materiais e propriedades de acordo com o tipo do Model Map
        if (type === "EC") {
          typesAvailable = typesAvailable.concat(types);
          typePropertiesAvailable = typePropertiesAvailable.concat(properties);
        } else {
          materialsAvailable = materialsAvailable.concat(types);
          materialPropertiesAvailable =
            materialPropertiesAvailable.concat(properties);
        }

        // Verifica e já existe em memória a definição de algum conteúdo para o arquivo ifc identificado na promise
        if (this.lastLibrariesEnabledWBS[fileId]) {
          // Complementa a informação já existente no ModelMapStore
          let fileIdContent = this.lastLibrariesEnabledWBS[fileId];
          fileIdContent[type] = value;
          this.lastLibrariesEnabledWBS[fileId] = fileIdContent;
        } else {
          // Cria e adiciona no ModelMapStore um objeto com as informações recuperadas no retorno da promise
          let fileIdContent = { [type]: value };
          this.lastLibrariesEnabledWBS[fileId] = fileIdContent;
        }
      }
    });
    // }
  }

  /** Abre a página de criar um model-map, limpando as variáveis usadas e */
  @action
  async openLayerCreateModelMap(type: EModelMapType) {
    TakeOffStore.resetTakeOffProperties();
    this.resetModelMapFormatProperties();
    await LibraryStore.fetchLibraries();
    await this.fetchModelMapProperties(type);
    TakeOffStore.setLayerToAddModelMap();
    this.typeSelected = type;
  }

  async openTakeOffToFixErrors(
    modelMap,
    isCategoryOrModelError: boolean,
    filterWithError: string
  ) {
    this.setModelMapSelected(modelMap);
    TakeOffStore.hasErrorFilter = true;

    await this.openTakeOff();

    setTimeout(() => {
      if (isCategoryOrModelError) {
        TakeOffStore.setSwipeTab(EModelMapTab.CONFIGURATION);
      } else if (filterWithError) {
        TakeOffStore.setSwipeTab(EModelMapTab.FILTER);
        const { takeOffModelMap } = TakeOffStore;

        if (takeOffModelMap && takeOffModelMap.data.filters) {
          takeOffModelMap.data.filters.forEach((clmn, ndx) => {
            if (clmn.field == filterWithError) {
              TakeOffStore.setSelectedColumn(clmn.field, ndx, null);
              WBSStore.rightMenuTab = ERightMenuTab.PROPERTIES;
            }
          });
        }
      } else {
        TakeOffStore.isFormating = true;
      }
    }, 1000);
  }

  async openTakeOff() {
    const modelMap = this.modelMapSelected;
    if (TakeOffStore.statusModelmap !== "Loading" && modelMap) {
      if (
        TakeOffStore.takeOffModelMap &&
        TakeOffStore.takeOffModelMap.id === modelMap.id &&
        TakeOffStore.layerTakeOffList == true
      )
        return;
      TakeOffStore.resetTakeOffProperties();
      TakeOffStore.takeOffLoading = true;

      this.resetModelMapFormatProperties();

      await TakeOffService.fetchTakeOffList(modelMap);
      this.loadModelFormat();
      TakeOffStore.fetchTakeOffFormatedList(); //

      TakeOffStore.layerTakeOffEdit = "";
      this.typeSelected = modelMap.data.type;
      TakeOffStore.takeOffLoading = false;
    }
  }

  @action
  private resetModelMapFormatProperties() {
    this.modelMapFormat = new Array();
    this.selectedFormatLine = undefined;
  }

  async duplicateModelMap(id: string, name: string) {
    const wbsId = WBSStore.WBS.id;
    const devId = DevelopmentStore.developmentId;

    if (devId && wbsId) {
      await axios.post(`${process.env.REACT_APP_COPY_MODELMAP}`, {
        wbsId: wbsId,
        modelMapId: id,
        modelMapName: name + i18n.modelMaps.menuLateral.copy,
        developmentId: devId,
      });
    }
  }

  /**
   * Aplica a pesquisa passada por parâmetro nas listas de model-maps pesquisados e para exibição.
   * Caso o valor informado seja uma string vazia, então o sistema passa todos os
   * model-maps do projeto para as listas citadas.
   */
  searchModelMaps(value: string): void {
    let modelMapsFiltered: FirebaseDocument<ModelMap>[] = [];
    if (value) {
      modelMapsFiltered = this.modelMaps.filter((lib) =>
        lib.data.name.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      modelMapsFiltered = [...this.modelMaps];
    }
    this.setSearchResult(value, modelMapsFiltered);
  }

  @action
  private setSearchResult(
    searchValue: any,
    modelMapsSearch: FirebaseDocument<ModelMap>[]
  ) {
    this.search = searchValue;
    this.modelMapsView = modelMapsSearch;
  }

  @action
  async deleteCheckedModelMaps() {
    const models = toJS(this.modelMapCheck);
    await Promise.all(
      models.map((model) => ModelMapService.deleteModelMapFromDataBase(model))
    );
    this.modelMapCheck = [];
    this.resetWhenDeleteModelMap();
  }

  async deleteModelMap() {
    const modelMap = this.modelMapSelected;
    if (modelMap) {
      await ModelMapService.deleteModelMapFromDataBase(modelMap);
      _.each(WBSStore.dataTable, function (row) {
        if (row && row.data && row.data.fromModelMap === modelMap.id) {
          row.data.fromModelMap = "";
          row.data.takeOffQuantity = [];
          row.data.modelMapFull = false;
        }
      });

      this.setIsCurrentWbsNeedToBeUpdated();
      this.resetWhenDeleteModelMap();
    }
  }

  @action
  resetWhenDeleteModelMap() {
    this.modelMapSelected = undefined;
    TakeOffStore.resetWhenDeleteModelMap();
    this.modelMapFormat = [];
  }

  /**
   * Função para atualizar os quantitativos dos model maps com warning na EAP
   */
  async updateCheckedModelMapsTakeOffIntoWBS() {
    let modelMaps = [...this.modelMapCheck];

    for (const modelMap of modelMaps) {
      await this.removeWbsFromWbsThatNeedToBeUpdated(modelMap);
      TakeOffStore.statusModelmap = "Loading";

      await TakeOffService.updateTakeOffIntoWBS(modelMap);
      TakeOffStore.statusModelmap = "";

      if (!WBSStore.updateResource) {
        WBSStore.updateResource = true;
      }
    }

    this.setIsCurrentWbsNeedToBeUpdated();
  }

  async removeWbsFromWbsThatNeedToBeUpdated(
    modelMap: FirebaseDocument<ModelMap>
  ) {
    try {
      const wbsId = WBSStore.currentWbsId;
      const currentModelMap = await ModelMapService.getModelMap(modelMap.id);

      if (currentModelMap) {
        const { costPlansNeedtoBeUpdated } = currentModelMap.data;
        if (costPlansNeedtoBeUpdated) {
          let index = costPlansNeedtoBeUpdated.indexOf(wbsId);
          if (index !== -1) {
            costPlansNeedtoBeUpdated.splice(index, 1);
            await ModelMapService.updateModelMapCostPlansNeedtoBeUpdated(
              modelMap.id,
              costPlansNeedtoBeUpdated
            );
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  addQuantityFromTakeOffItem(line) {
    if (line) TakeOffStore.addQuantityFromTakeOffItem(line);
  }

  insertTakeOffIntoWBS(modelMap: FirebaseDocument<ModelMap>) {
    TakeOffService.fetchTakeOffIntoWBS(modelMap);
    ToolbarStore.handelActions({
      originalOperation: "fetchTakeOffIntoWBS",
      undoOperation: "deleteWBSRow",
      data: {
        undoFunction: {
          function: "deleteWBSRow",
          params: [],
          selectedLine: [],
        },
        originalFunction: {
          function: "fetchTakeOffIntoWBS",
          params: [modelMap],
        },
      },
    });
    if (!WBSStore.updateResource) {
      WBSStore.updateResource = true;
    }
  }

  /**
   * Função executada no clique para atualizar as informações da EAP de um model map específico
   */
  async updateTakeOffIntoWBS(modelMap: FirebaseDocument<ModelMap>) {
    ModelMapService.removeCurrentWbsCostPlansNeedtoBeUpdated(modelMap.id);
    TakeOffStore.statusModelmap = "Loading";
    await TakeOffService.updateTakeOffIntoWBS(modelMap).then(() => {
      TakeOffStore.statusModelmap = "";
    });
    if (!WBSStore.updateResource) {
      WBSStore.updateResource = true;
    }
    // Após serem feitas as alterações do model map, o recalculo é chamado.
    await WBSStore.changeDecimalAndRule();
  }

  async getModelMap(modelMapID) {
    return ModelMapService.getModelMap(modelMapID);
  }
}

const store = new ModelMapStore();
export { store as ModelMapStore };



// WEBPACK FOOTER //
// ./src/modules/modelMap/ModelMapStore.ts